import { useState, useEffect } from 'react';
import api from '../config/axios'; // Use the configured Axios instance
import { formatErrorMessage } from '../utils/apiUtils';

// Simple cache for properties
const propertiesCache = new Map();

/**
 * Hook to fetch table properties (e.g., columnConfig, filterConfig) from the backend.
 * Assumes a backend endpoint like '/api/table-properties/:tableName'.
 * @param {string} tableName - The name of the table to fetch properties for.
 * @param {boolean} enabled - Whether the hook should execute the fetch (default: true).
 * @param {string} apiPropertiesEndpoint - The API endpoint to fetch properties from.
 * @returns {{ properties: Array|Object|null, loading: boolean, error: string|null }}
 */
const useFetchProperties = (
    tableName,
    enabled = true,
    apiPropertiesEndpoint = '/table-properties' // Example endpoint
) => {
    const [properties, setProperties] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!tableName || !enabled) {
            setProperties(null); // Reset if disabled or no tableName
            return;
        }

        const cacheKey = `${apiPropertiesEndpoint}/${tableName}`;

        // Check cache first
        if (propertiesCache.has(cacheKey)) {
            setProperties(propertiesCache.get(cacheKey));
            return;
        }

        const fetchProperties = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await api.get(`${apiPropertiesEndpoint}/${tableName}`);
                const fetchedProperties = response.data; // Adjust based on actual API response structure

                if (!fetchedProperties) {
                    throw new Error('Nessuna proprietà restituita dal backend.');
                }

                propertiesCache.set(cacheKey, fetchedProperties);
                setProperties(fetchedProperties);

            } catch (err) {
                console.error(`[useFetchProperties] Errore fetch proprietà per ${tableName}:`, err);
                const formattedError = formatErrorMessage(err);
                setError(formattedError);
                setProperties(null); // Reset properties on error
            } finally {
                setLoading(false);
            }
        };

        fetchProperties();

    }, [tableName, enabled, apiPropertiesEndpoint]); // Dependencies

    return { properties, loading, error };
};

export default useFetchProperties;